import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import {
  aeroWorkbenchApi,
  type AeroInverseDesignObjective,
  type AeroInverseDesignVariable,
  type AeroInverseLiftTargetPoint,
  type AeroLiftingLineInverseDesignResponse,
  type AeroLiftingLineSettings,
  type AeroSectionSolveRow,
} from "../../api/aeroWorkbench";
import type { WingDefinition } from "./api";
import { planformShape } from "./sectionCampaignModel";
import { fitQuickLiftingLinePanelBudget } from "./wingModel";

type ObjectiveMode = "distribution" | "ld-max" | "ld-target";
type RunState = "idle" | "running" | "done" | "warning" | "error";

export interface PreparedThreadInverseDesignContext {
  projectId: string;
  geometryArtifactVersionId: string;
  sectionModelArtifactVersionId: string;
}

const DEFAULT_TARGET: AeroInverseLiftTargetPoint[] = [
  { eta: 0, lift_per_span_n_m: 1_000 },
  { eta: 0.25, lift_per_span_n_m: 968 },
  { eta: 0.5, lift_per_span_n_m: 866 },
  { eta: 0.75, lift_per_span_n_m: 661 },
  { eta: 1, lift_per_span_n_m: 0 },
];

function variableKey(variable: Pick<AeroInverseDesignVariable, "kind" | "station_id">): string {
  return `${variable.kind}:${variable.station_id}`;
}

function finiteMetric(
  metrics: Record<string, number | boolean | string>,
  ...names: string[]
): number | null {
  for (const name of names) {
    const value = metrics[name];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

function evidenceRevisionToken(rows: readonly AeroSectionSolveRow[]): string {
  let hash = 2_166_136_261;
  for (const row of rows) {
    const token = [
      row.point_id,
      row.dataset_split,
      row.status,
      row.prediction_receipt?.observation_key,
      row.prediction?.cl,
      row.prediction?.cd,
    ].join(":");
    for (let index = 0; index < token.length; index += 1) {
      hash ^= token.charCodeAt(index);
      hash = Math.imul(hash, 16_777_619);
    }
  }
  return `${rows.length}:${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

function bestWingAppliedToDraft(draft: WingDefinition, value: Record<string, unknown>): WingDefinition {
  const candidateStations = Array.isArray(value.stations) ? value.stations : [];
  const byId = new Map<string, Record<string, unknown>>();
  for (const candidate of candidateStations) {
    if (candidate && typeof candidate === "object" && typeof (candidate as Record<string, unknown>).id === "string") {
      byId.set((candidate as Record<string, unknown>).id as string, candidate as Record<string, unknown>);
    }
  }
  return {
    ...draft,
    stations: draft.stations.map((station) => {
      const best = byId.get(station.id);
      const chord = best?.chord;
      const twist = best?.twist_deg;
      return {
        ...station,
        chord: typeof chord === "number" && Number.isFinite(chord) ? chord : station.chord,
        twist_deg: typeof twist === "number" && Number.isFinite(twist) ? twist : station.twist_deg,
      };
    }),
  };
}

function ObjectiveLiftPlot({
  response,
  target,
  normalized,
}: {
  response: AeroLiftingLineInverseDesignResponse;
  target: AeroInverseLiftTargetPoint[];
  normalized: boolean;
}) {
  const wake = response.best.wake;
  const filamentByPanel = new Map(wake.wake_filaments.map((filament) => [filament.panel_id, filament]));
  const authoredSegments = wake.result.span_loads.flatMap((load) => {
    if (load.mirrored !== false) return [];
    const filament = filamentByPanel.get(load.panel_id);
    if (!filament || filament.mirrored !== false) return [];
    const startY = filament.bound_start_m[1];
    const endY = filament.bound_end_m[1];
    const width = Math.abs(endY - startY);
    return Number.isFinite(width) && width > 1e-12 ? [{ load, startY, endY, width }] : [];
  });
  const halfSpan = Math.max(1e-9, ...authoredSegments.flatMap(({ startY, endY }) => [Math.abs(startY), Math.abs(endY)]));
  const actual = authoredSegments.map(({ load, startY, endY, width }) => {
    return {
      eta: Math.max(0, Math.min(1, Math.abs(0.5 * (startY + endY)) / halfSpan)),
      value: load.lift_n / width,
      weight: width / halfSpan,
    };
  }).sort((left, right) => left.eta - right.eta);
  const actualArea = Math.max(1e-9, actual.reduce((sum, point) => sum + point.value * point.weight, 0));
  const targetArea = Math.max(1e-9, target.slice(1).reduce((sum, point, index) => {
    const left = target[index]!;
    return sum + 0.5 * (point.eta - left.eta) * (left.lift_per_span_n_m + point.lift_per_span_n_m);
  }, 0));
  const displayActual = actual.map((point) => ({
    eta: point.eta,
    value: normalized ? point.value / actualArea : point.value,
  }));
  const displayTarget = target.map((point) => ({
    eta: point.eta,
    value: normalized ? point.lift_per_span_n_m / targetArea : point.lift_per_span_n_m,
  }));
  const maxValue = Math.max(1e-9, ...displayActual.map((point) => point.value), ...displayTarget.map((point) => point.value));
  const width = 640;
  const height = 210;
  const margin = { top: 18, right: 18, bottom: 34, left: 54 };
  const x = (eta: number) => margin.left + eta * (width - margin.left - margin.right);
  const y = (value: number) => margin.top + (1 - value / maxValue) * (height - margin.top - margin.bottom);
  const path = (points: Array<{ eta: number; value: number }>) => points
    .map((point, index) => `${index ? "L" : "M"}${x(point.eta).toFixed(2)} ${y(point.value).toFixed(2)}`)
    .join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Target and optimized spanwise lift distributions">
      <rect
        x={margin.left}
        y={margin.top}
        width={width - margin.left - margin.right}
        height={height - margin.top - margin.bottom}
        fill="var(--fc-surface-2)"
        stroke="var(--fc-border)"
      />
      {[0, 0.25, 0.5, 0.75, 1].map((eta) => (
        <g key={eta}>
          <line x1={x(eta)} x2={x(eta)} y1={margin.top} y2={height - margin.bottom} stroke="var(--fc-border)" strokeWidth=".7" />
          <text x={x(eta)} y={height - 12} textAnchor="middle">{eta.toFixed(2)}</text>
        </g>
      ))}
      <path d={path(displayTarget)} fill="none" stroke="var(--fc-warning)" strokeDasharray="5 4" strokeWidth="2" />
      <path d={path(displayActual)} fill="none" stroke="var(--fc-primary-bright)" strokeWidth="2" />
      {displayActual.map((point, index) => <circle key={index} cx={x(point.eta)} cy={y(point.value)} r="2" fill="var(--fc-primary-bright)" />)}
      <text x="12" y={(margin.top + height - margin.bottom) / 2} textAnchor="middle" transform={`rotate(-90 12 ${(margin.top + height - margin.bottom) / 2})`}>
        {normalized ? "normalized L′" : "L′ (N/m)"}
      </text>
      <text x={(margin.left + width - margin.right) / 2} y={height - 1} textAnchor="middle">half-span η</text>
    </svg>
  );
}

export function AircraftObjectivePanel({
  wing,
  sectionRows,
  alphaDeg,
  speedMps,
  aircraftModelId,
  inputSliderNames,
  ready,
  prepareThreadContext,
  onApplyWing,
}: {
  wing: WingDefinition;
  sectionRows: AeroSectionSolveRow[];
  alphaDeg: number;
  speedMps: number;
  aircraftModelId: string;
  inputSliderNames: string[];
  ready: boolean;
  prepareThreadContext?: (signal: AbortSignal) => Promise<PreparedThreadInverseDesignContext>;
  onApplyWing: (wing: WingDefinition) => void;
}) {
  const availableVariables = useMemo<AeroInverseDesignVariable[]>(() => wing.stations.flatMap((station) => [
    {
      kind: "station_twist_deg" as const,
      station_id: station.id,
      lower: Math.max(-45, station.twist_deg - 4),
      upper: Math.min(45, station.twist_deg + 4),
    },
    {
      kind: "station_chord_m" as const,
      station_id: station.id,
      lower: Math.max(0.01, station.chord * 0.7),
      upper: station.chord * 1.3,
    },
  ]), [wing.stations]);
  const objectiveSolverWing = useMemo(() => fitQuickLiftingLinePanelBudget(wing), [wing]);
  const objectiveHorseshoes = Math.max(
    1,
    2 * Math.max(0, objectiveSolverWing.stations.length - 1) * objectiveSolverWing.span_panels_per_segment,
  );
  const safeEvaluationLimit = Math.max(1, Math.min(
    24,
    Math.floor(6 * (96 / objectiveHorseshoes) ** 2),
    Math.floor((96 * 6) / objectiveHorseshoes),
    Math.floor(16_000 / 400),
  ));
  const evaluationOptions = [...new Set([1, 2, 4, 6, 8, 12, 16, 24, safeEvaluationLimit])]
    .filter((value) => value <= safeEvaluationLimit)
    .sort((left, right) => left - right);
  const [mode, setMode] = useState<ObjectiveMode>("distribution");
  const [normalized, setNormalized] = useState(true);
  const [targetPoints, setTargetPoints] = useState(DEFAULT_TARGET);
  const [targetRatio, setTargetRatio] = useState(18);
  const [evaluationBudget, setEvaluationBudget] = useState(() => Math.min(12, safeEvaluationLimit));
  const [selectedKeys, setSelectedKeys] = useState<string[]>(() => availableVariables
    .filter((variable) => variable.kind === "station_twist_deg")
    .slice(0, 8)
    .map(variableKey));
  const [state, setState] = useState<RunState>("idle");
  const [message, setMessage] = useState("Choose a target and bounded station variables. No new 2D evidence is generated.");
  const [result, setResult] = useState<AeroLiftingLineInverseDesignResponse | null>(null);
  const [resultArtifactVersionId, setResultArtifactVersionId] = useState<string | null>(null);
  const [resultKey, setResultKey] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const selectedVariables = availableVariables.filter((variable) => selectedSet.has(variableKey(variable))).slice(0, 8);
  const objective = useMemo<AeroInverseDesignObjective>(() => mode === "distribution"
    ? { kind: "match_lift_distribution", target_points: targetPoints, normalize: normalized }
    : mode === "ld-target"
      ? { kind: "lift_to_drag", mode: "target", target_ratio: targetRatio }
      : { kind: "lift_to_drag", mode: "maximize" }, [mode, normalized, targetPoints, targetRatio]);
  const settings = useMemo<AeroLiftingLineSettings>(() => ({
    max_iterations: 400,
    relaxation: 0.15,
    relative_tolerance: 1e-5,
    vortex_core_radius_m: 0.05,
    wake_length_m: Math.max(20, planformShape(wing).halfSpan * 3),
    include_mirror_y: true,
  }), [wing]);
  const evidenceRevision = useMemo(() => evidenceRevisionToken(sectionRows), [sectionRows]);
  const inputKey = JSON.stringify({
    wing,
    alphaDeg,
    speedMps,
    objective,
    selectedVariables,
    evaluationBudget,
    evidenceRevision,
  });
  const stale = result != null && resultKey !== inputKey;

  useEffect(() => () => abortRef.current?.abort(), []);
  useEffect(() => {
    setEvaluationBudget((current) => Math.min(current, safeEvaluationLimit));
  }, [safeEvaluationLimit]);
  useEffect(() => {
    const valid = new Set(availableVariables.map(variableKey));
    setSelectedKeys((current) => {
      const kept = current.filter((key) => valid.has(key)).slice(0, 8);
      return kept.length ? kept : availableVariables
        .filter((variable) => variable.kind === "station_twist_deg")
        .slice(0, 8)
        .map(variableKey);
    });
  }, [availableVariables]);

  const toggleVariable = (key: string) => {
    setSelectedKeys((current) => current.includes(key)
      ? current.filter((candidate) => candidate !== key)
      : current.length < 8 ? [...current, key] : current);
  };

  const run = async () => {
    if (!ready || !selectedVariables.length) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState("running");
    setMessage(prepareThreadContext
      ? `Preparing canonical Thread inputs, then evaluating up to ${evaluationBudget} relaxed free wakes…`
      : `Evaluating up to ${evaluationBudget} relaxed free wakes…`);
    try {
      const condition = {
        speed_mps: speedMps,
        alpha_deg: alphaDeg,
        beta_deg: 0,
        density_kg_m3: 1.225,
        dynamic_viscosity_pa_s: 1.81e-5,
        speed_of_sound_mps: 340,
      };
      const optimizer = {
        max_evaluations: evaluationBudget,
        initial_step_fraction: 0.25,
        min_step_fraction: 0.02,
      };
      let response: AeroLiftingLineInverseDesignResponse;
      if (prepareThreadContext) {
        const context = await prepareThreadContext(controller.signal);
        if (controller.signal.aborted) return;
        const runId = globalThis.crypto.randomUUID();
        const persisted = await aeroWorkbenchApi.runThreadInverseDesign({
          project_id: context.projectId,
          idempotency_key: `aero:inverse-design:${runId}`,
          target: {
            logical_id: runId,
            name: `Inverse design · ${objective.kind === "match_lift_distribution" ? "lift distribution" : "lift to drag"}`,
            metadata: { aircraft_model_instance_id: aircraftModelId },
          },
          geometry_artifact_version_id: context.geometryArtifactVersionId,
          section_model_artifact_version_id: context.sectionModelArtifactVersionId,
          condition,
          settings,
          objective,
          variables: selectedVariables,
          optimizer,
        }, controller.signal);
        response = persisted.content;
        setResultArtifactVersionId(persisted.artifact_version_id);
      } else {
        response = await aeroWorkbenchApi.inverseDesignLiftingLine({
          wing: objectiveSolverWing as unknown as Record<string, unknown>,
          section_rows: sectionRows,
          aircraft_model_instance_id: aircraftModelId,
          input_slider_names: inputSliderNames,
          condition,
          settings,
          objective,
          variables: selectedVariables,
          optimizer,
        }, controller.signal);
        setResultArtifactVersionId(null);
      }
      if (controller.signal.aborted) return;
      setResult(response);
      setResultKey(inputKey);
      setState(response.best.feasible ? "done" : "warning");
      const outsideDomainCount = response.best.metrics.outside_domain_station_count;
      const outsideDomainIds = response.best.metrics.outside_domain_station_ids;
      const outsideDomainLabel = (
        typeof outsideDomainCount === "number"
        && outsideDomainCount <= 3
        && typeof outsideDomainIds === "string"
        && outsideDomainIds
      ) ? outsideDomainIds : `${outsideDomainCount} stations`;
      setMessage(response.best.feasible
        ? `Best of ${response.provenance.evaluation_count} deterministic evaluations. Review before applying the wing.`
        : typeof outsideDomainCount === "number" && outsideDomainCount > 0
          ? `Best candidate leaves the aircraft section-model domain at ${outsideDomainLabel}. Add exact 2D evidence there and search again; Apply is guarded.`
          : "The best candidate remains numerically guarded; inspect the wake warnings before applying it.");
    } catch (error) {
      if (controller.signal.aborted) return;
      setState("error");
      setMessage(error instanceof Error ? error.message : "Inverse-design search failed.");
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const metrics = result?.best.metrics ?? {};
  const bestLd = finiteMetric(metrics, "lift_to_drag", "lift_drag_ratio", "best_lift_to_drag");
  const distributionError = finiteMetric(metrics, "normalized_rmse", "distribution_rmse", "rmse_n_m", "rmse");
  const outsideDomainCount = finiteMetric(metrics, "outside_domain_station_count");

  return (
    <section className="section-analysis-panel aircraft-objective-panel" data-testid="aircraft-objective-panel">
      <header className="aircraft-objective-header">
        <span>
          <strong>Design to an aerodynamic target</strong>
          <small>aircraft section NN → relaxed free wake · deterministic bounded search</small>
        </span>
        <label>
          <span>Objective</span>
          <select value={mode} onChange={(event) => setMode(event.target.value as ObjectiveMode)}>
            <option value="distribution">Match lift distribution</option>
            <option value="ld-max">Maximize L/D</option>
            <option value="ld-target">Hit target L/D</option>
          </select>
        </label>
        <label>
          <span>Budget · max {safeEvaluationLimit}</span>
          <select value={evaluationBudget} onChange={(event) => setEvaluationBudget(Number(event.target.value))}>
            {evaluationOptions.map((value) => <option key={value} value={value}>{value} {value === 1 ? "wake" : "wakes"}</option>)}
          </select>
        </label>
        <button type="button" onClick={run} disabled={!ready || !selectedVariables.length || state === "running"}>
          {state === "running" ? "Optimizing…" : "Run target search"}
        </button>
      </header>
      <div className="aircraft-objective-body">
        <aside className="aircraft-objective-setup">
          {mode === "distribution" ? (
            <section>
              <header><strong>Target L′</strong><label><input type="checkbox" checked={normalized} onChange={(event) => setNormalized(event.target.checked)} /> normalize shape</label></header>
              <div className="aircraft-target-points">
                {targetPoints.map((point, index) => (
                  <label key={point.eta}>
                    <span>η {point.eta.toFixed(2)}</span>
                    <input
                      aria-label={`Target lift at eta ${point.eta.toFixed(2)}`}
                      type="number"
                      min={0}
                      step={normalized ? 10 : 100}
                      value={point.lift_per_span_n_m}
                      onChange={(event) => setTargetPoints((current) => current.map((candidate, candidateIndex) => candidateIndex === index
                        ? { ...candidate, lift_per_span_n_m: Math.max(0, Number(event.target.value) || 0) }
                        : candidate))}
                    />
                  </label>
                ))}
              </div>
              <small>{normalized ? "Values define shape; overall lift magnitude is ignored." : "Physical target values are N/m."}</small>
            </section>
          ) : mode === "ld-target" ? (
            <section>
              <header><strong>Target efficiency</strong></header>
              <label className="aircraft-ld-target"><span>L/D</span><input type="number" min={0.1} max={100000} step={0.5} value={targetRatio} onChange={(event) => setTargetRatio(Math.max(0.1, Number(event.target.value) || 0.1))} /></label>
            </section>
          ) : (
            <section className="aircraft-objective-note">
              <strong>Maximize total L/D</strong>
              <small>Profile and induced drag both come from each relaxed-wake evaluation.</small>
            </section>
          )}
          <section>
            <header><strong>Design variables</strong><small>{selectedVariables.length}/8</small></header>
            <div className="aircraft-variable-list">
              {wing.stations.map((station) => {
                const twistKey = variableKey({ kind: "station_twist_deg", station_id: station.id });
                const chordKey = variableKey({ kind: "station_chord_m", station_id: station.id });
                return (
                  <div key={station.id}>
                    <strong>{station.id}</strong>
                    <label><input type="checkbox" checked={selectedSet.has(twistKey)} onChange={() => toggleVariable(twistKey)} /> twist ±4°</label>
                    <label><input type="checkbox" checked={selectedSet.has(chordKey)} onChange={() => toggleVariable(chordKey)} /> chord ±30%</label>
                  </div>
                );
              })}
            </div>
          </section>
        </aside>
        <main className="aircraft-objective-result">
          {result ? (
            <>
              <header>
                <span><strong>Best candidate</strong><small>{result.provenance.termination} · {result.provenance.evaluation_count}/{result.provenance.evaluation_budget} evaluations{resultArtifactVersionId ? ` · Thread ${resultArtifactVersionId.slice(0, 8)}` : ""}</small></span>
                <dl>
                  {bestLd != null ? <div><dt>L/D</dt><dd>{bestLd.toFixed(2)}</dd></div> : null}
                  {distributionError != null ? <div><dt>{normalized ? "shape RMSE" : "RMSE"}</dt><dd>{distributionError.toPrecision(3)}</dd></div> : null}
                  {outsideDomainCount ? <div><dt>model domain</dt><dd>{outsideDomainCount} outside</dd></div> : null}
                  <div><dt>score</dt><dd>{result.best.score.toPrecision(4)}</dd></div>
                </dl>
                <button type="button" disabled={stale || !result.best.feasible} onClick={() => onApplyWing(bestWingAppliedToDraft(wing, result.best.wing))}>
                  {stale ? "Inputs changed" : "Apply best wing"}
                </button>
              </header>
              {result.objective.kind === "match_lift_distribution" ? (
                <ObjectiveLiftPlot response={result} target={result.objective.target_points} normalized={result.objective.normalize} />
              ) : (
                <div className="aircraft-objective-history" aria-label="Inverse-design evaluation history">
                  {result.history.map((trial) => (
                    <div key={trial.evaluation_index} className={trial.evaluation_index === result.history.length ? "is-latest" : undefined}>
                      <span>#{trial.evaluation_index}</span><i style={{ "--objective-score": trial.score } as CSSProperties} /><strong>{trial.score.toPrecision(4)}</strong><em>{trial.feasible ? "valid" : "guarded"}</em>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="section-analysis-empty">
              <strong>{ready ? "Define the target, then search" : "Aircraft section evidence is not ready"}</strong>
              <span>{ready
                ? "Each evaluation reuses the aircraft section model and solves a relaxed free wake. The geometry changes only when you apply the best candidate."
                : "Run enough converged exact 2D section cases to fit the aircraft model first."}</span>
            </div>
          )}
        </main>
      </div>
      <footer className={`section-diagnostic-status is-${state}`} role={state === "error" ? "alert" : "status"}>
        <span>{message}</span>
        <em>{stale ? "result stale · inputs changed" : prepareThreadContext ? "Thread-persisted search · no new 2D evidence" : "preview search · no new 2D evidence"}</em>
      </footer>
    </section>
  );
}
